import type { Anchor, FillLayerSpecification } from "mapbox-gl";

import {
  LayerId,
  SingleLayerId,
  SingleLayerWithDateLayerId,
} from "../../InteractiveMap/types";

// Display values for field borders
export const FIELD_LAYER_FILL_OPACITY = 0.4;
export const FIELD_LAYER_LABEL_ANCHOR: Anchor = "center";
export const FIELD_LAYER_LABEL_OFFSET = [0, 0];
export const FIELD_LAYER_FILL_COLOR = "rgb(255, 255, 255)";

// Asana ticket: https://app.asana.com/0/1208505234259343/1208811375977583/f
export const NA_COLOR = "#888888";

// See https://colorbrewer2.org/ for more map safe color palettes
export const BAD_COLOR = "#D7191C";
export const WARN_COLOR = "#FFFFBF";
export const GOOD_COLOR = "#1A9641";
export const EXTRA_GOOD_COLOR = "#3288bd";

// 'Bad' values to 'Good' values
export const OPINIONATED_PALETTE = [
  BAD_COLOR,
  "#FDAE61",
  WARN_COLOR,
  "#A6D96A",
  GOOD_COLOR,
];
export const NEUTRAL_PALETTE = [
  "#edf8fb",
  "#b3cde3",
  "#8c96c6",
  "#8856a7",
  "#810f7c",
];

export const NEUTRAL_PALETTE_FOR_WATER = [
  "#f1eef6",
  "#d0d1e6",
  "#a6bddb",
  "#74a9cf",
  "#2b8cbe",
  "#045a8d",
];

export const mergeArrays = (
  arr1: string[] | number[] | string[][],
  arr2: string[],
) => {
  const mergedArr = [];
  for (let i = 0; i < arr1.length; i++) {
    mergedArr.push(arr1[i], arr2[i]);
  }
  return mergedArr;
};

export const FIVE_STOP_RANGE: [string][] = [
  ["Very Low"],
  ["Low"],
  ["Medium"],
  ["High"],
  ["Very High"],
];
export const getFiveStopPaintStyle = (dataKey: string) => [
  ["get", dataKey],
  ...mergeArrays(FIVE_STOP_RANGE, OPINIONATED_PALETTE),
  NA_COLOR,
];

export const THREE_STOP_VALUES_LABELS: [string][] = [
  ["Low"],
  ["Adequate"],
  ["High"],
];

export const TWO_STOP_VALUES_LABELS: [string][] =
  THREE_STOP_VALUES_LABELS.slice(0, 2);

export const getThreeStopPaintStyle = (dataKey: string) => [
  ["get", dataKey],
  ["Low"],
  BAD_COLOR,
  ["Adequate"],
  WARN_COLOR,
  ["High"],
  GOOD_COLOR,
  NA_COLOR,
];

export const getTwoStopPaintStyle = (dataKey: string) => [
  ["get", dataKey],
  ["Low"],
  BAD_COLOR,
  ["Adequate"],
  GOOD_COLOR,
  NA_COLOR,
];

export type DataCategorization = {
  id: string;
  label: string;
  subLabel?: string | JSX.Element;
  // TODO: Struggling to make a generic function here that would accept an
  // explicit type (i.e `<number>(value) => value > 100`)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueMatch: (value: any) => boolean;
  color: string[];
  hideIfZero?: boolean;
};

export const makeDataCategoriesValueMatch = (
  colorArray: string[],
  matchValuesLabels: [string][],
): DataCategorization[] => {
  const categories: DataCategorization[] = [];
  if (colorArray.length !== matchValuesLabels.length) {
    throw new Error(
      `Color array and match values/labels must be the same length`,
    );
  }
  for (let i = 0; i < colorArray.length; i++) {
    categories.push({
      id: matchValuesLabels[i][0],
      label: matchValuesLabels[i][0],
      valueMatch: (value) => value === matchValuesLabels[i][0],
      color: [colorArray[i]],
    });
  }
  return categories;
};

export type BaseMapLayerData = {
  name: string;
  description?: React.ReactNode;
  legend?: React.ReactNode;
};

export type BaseMapGeoJsonLayerData = BaseMapLayerData & {
  // used for labels
  visualizationKey: string;
  mapboxPaintStyle: FillLayerSpecification["paint"];
  histogramTitle?: string;
  // used for histogram/legend value categorization
  // if not provided, visualizationKey will be used - these may be different
  // if the values are already binned in the data (i.e. 0-5 = "Low")
  categorizationKey?: string;
  categorization?: DataCategorization[];
};

export type SsurgoDatatype = BaseMapGeoJsonLayerData & {
  id: SingleLayerId;
};

export type NutrientManagementDatatype = BaseMapGeoJsonLayerData & {
  id: LayerId;
  partnerLayerIds?: LayerId[]; // corresponding testing/prescription layers
};

export type SatelliteImageryDatatype = BaseMapLayerData & {
  id: SingleLayerWithDateLayerId;
};
